<template>
    <Card class="card" style="overflow: hidden"
        :class="authStore.amIAdmin() || bookData.available ? 'available_book' : 'unavailable_book'">

        <template #header>
            <div class="header-info flex justify-content-around align-items-center p-0">
                <Checkbox v-model="selected" v-if="canBeSelected" :binary="true" @change="manageCheckEvent" />
                <div v-else class="py-1 text-blue-700">الكتاب غير متاح</div>
            </div>
            <Divider class="mb-0 mt-2" />
        </template>

        <template #title>
            <div class="flex justify-content-around align-items-center gap-1 p-1 text-sm tooltip-container mt-1">
                <div class="border-round surface-ground py-2 flex justify-content-center align-items-center w-full">
                    <span class="tooltiptext" v-if="bookData.title !== bookData.abvTitle">
                        <span>{{ bookData.title }}</span>
                    </span>
                    {{ bookData.abvTitle }}
                </div>
            </div>
            <Divider class="mb-0 mt-1" />
        </template>

        <template #content>
            <div class="text-center " style="font-weight: bold; font-size: 0.8rem">
                <div class="flex flex-wrap justify-content-center book_buttons_container">
                    <div v-if="shouldShowOwnerAvatar" class="w-full">
                        <img :src="bookData.owner.imageUrl" class="user_avatar mt-1"
                            @click="showUserInfo(bookData.owner.id)" />
                        <Divider class="mb-2 mt-0" />
                    </div>
                    <div>
                        <Button v-if="authStore.amIAdmin() && bookData.involvedInOperation" class="round_button"
                            @click="showBooksOperations">
                            <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
                        </Button>
                    </div>
                </div>
                <img class="book-cover" style="width: 7.8em; height: 10em; margin-top: 5px;" :src="bookData.imageUrl" />

                <div
                    class="border-round surface-50 py-2 flex justify-content-center align-items-center w-full tooltip-container text-blue-500">
                    <span class="tooltiptext" v-if="bookData.author !== bookData.abvAuthor">
                        <span>{{ bookData.author }}</span>
                    </span>
                    {{ bookData.abvAuthor }}
                </div>
                
            </div>
        </template>

        <template #footer>
            <Divider class="m-0 mb-2" />

            <div class="flex justify-content-center align-items-center gap-2">
                <div class="flex align-items-center justify-content-center bg-blue-100 border-round p-2">
                    <i :class="['pi', 'pi-info-circle', 'text-blue-500']" style="cursor: pointer; font-size: 1.25rem;"
                        @click="showBookInfo" />
                </div>
                <div v-for="c in bookData.categories" :key="c"
                    class="border-round surface-50 py-2 flex justify-content-center align-items-center w-full gap-2">
                    <i class="pi pi-tag text-blue-500" style="font-size: 0.75rem" />
                    <span class="text-sm text-blue-500 ">{{ c.name }}</span>
                </div>
            </div>

        </template>
    </Card>
</template>

<script setup>
import { ref, watch, onBeforeMount, computed } from 'vue'
import { useDialog } from 'primevue/usedialog'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'
import UserOverview from '@/components/UserOverview.vue'
import BookDetails from '@/components/BookDetails.vue'
import OperationsOverview from '@/components/OperationsOverview'
import operationService from '@/service/OperationService'

const props = defineProps({
    data: {
        type: Object,
        required: true,
        default: () => { }
    },
    selectable: {
        type: Boolean,
        required: false,
        default: () => false
    }
})

const dialog = useDialog()
const authStore = useAuthStore()
const i18n = useI18n()

const bookData = ref()
const selected = ref(false)

const emit = defineEmits(['book-selected', 'book-unselected'])

onBeforeMount(() => {
    bookData.value = props.data
    selected.value = props.data.selected
})

watch(() => props.data.selected, newValue => {
    selected.value = newValue
})

const manageCheckEvent = () => {
    bookData.value.selected = selected.value
    if (bookData.value.selected === true) {
        emit('book-selected', bookData.value.id)
    } else {
        emit('book-unselected', bookData.value.id)
    }
}

const showUserInfo = (userId) => {
    dialog.open(UserOverview, {
        props: {
            header: i18n.t('USER_INFO'),
            style: {
                width: '20em',
            },
            breakpoints: {
                '960px': '75vw',
                '640px': '90vw'
            },
            modal: true
        },
        data: {
            userId: userId,
        }
    })
}

const showBookInfo = () => {
    dialog.open(BookDetails, {
        props: {
            header: i18n.t('BOOK_DETAIL'),
            style: {
                width: '20em',
            },
            breakpoints: {
                '960px': '75vw',
                '640px': '90vw'
            },
            modal: true
        },
        data: {
            book: bookData.value,
        }
    })
}

const shouldShowOwnerAvatar = computed(() => {
    return authStore.user.id !== bookData.value.owner.id
})

const canBeSelected = computed(() => {
    return props.selectable && bookData.value.available
})

const showBooksOperations = async () => {
    const response = await operationService.findOperationsByBook(bookData.value.id)
    const operations = response.result.collection
    dialog.open(OperationsOverview, {
        props: {
            header: i18n.t('BOOK_OPERATIONS'),
            style: {
                width: '20em',
            },
            breakpoints: {
                '960px': '75vw',
                '640px': '90vw'
            },
            modal: true
        },
        data: {
            operations: operations,
        }
    })
}

</script>

<style scoped>
.card {
    min-width: 175px;
    background: var(--surface-section);
    border: 1px solid var(--surface-border);
    padding: 0.5rem !important;
    margin-bottom: 2rem;
    box-shadow: var(--card-shadow);
    border-radius: 12px;

    &:last-child {
        margin-bottom: 0;
    }
}

.book {
    border-radius: 18px !important;
    border: 0.1px solid #e5e3d7 !important;
}

/* .available_book {
    background: #e5e3d7 !important;
} */

.unavailable_book {
    background: #c0c1ba !important;
} 

.book:deep(.p-card-body) {
    padding: 0em !important;
}


.tooltip-container {
    position: relative;
}

.tooltip-container .tooltiptext {
    visibility: hidden;
    width: 160px;
    background-color: var(--blue-400);
    color: #fff;
    text-align: center;
    padding: 5px 0;
    border-radius: 4px;

    position: absolute;
    z-index: 1;
}

.tooltip-container:hover .tooltiptext {
    visibility: visible;
}

.user_avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    cursor: pointer;
}

.book_buttons_container>div {
    margin: 0 5px;
}
</style>